<template>
  <div class="edit-video-lecture">
    <div class="edit-video-lecture__body container">
      <div class="row">
        <div class="col-12">
          <div class="edit-video-lecture__header-title typography--color-primary text-center">
            <span v-if="lecture">
              {{ $t('publishers.modal.edit_video_lecture.title') }}
            </span>
            <span v-else>
              {{ $t('publishers.modal.new_video_lecture.title') }}
            </span>
          </div>

          <div class="edit-video-lecture__video-preview w-100" v-if="videoEmbed">
            <div class="w-100" v-html="videoEmbed"/>
          </div>

          <div class="row" v-if="loading">
            <div class="col-12 text-center">
              <LoadingBounce />
              <div class="typography--body-2 typography--color-disabled pb-5">
                {{ $t('common.loading') }}
              </div>
            </div>
          </div>

          <div class="edit-video-lecture__form" v-else>
            <!-- subject select -->
            <div class="form-group">
              <label for="subject-select"
                    class="typography--body-3 typography--color-secondary typography--font-weight-600">
                {{ $t('publishers.modal.new_series.subject') }}
              </label>

              <select v-model="subjectId"
                      id="subject-select"
                      class="form-control"
                      @change="onSubjectSelect"
                      :disabled="subjectSelectDisabled">
                <option disabled value="">
                  {{ $t('publishers.modal.new_series.subject_placeholder') }}
                </option>
                <option v-for="sjId in subjectIds.filter(id => id !== '0' && id !== '9999')"
                        :key="sjId"
                        :value="sjId">
                  {{ getTranslatedName(subjectMap[sjId]) }}
                </option>
              </select>
            </div>

            <!-- grade select -->
            <div class="form-group">
              <label for="grade-select"
                    class="typography--body-3 typography--color-secondary typography--font-weight-600">
                {{ $t('publishers.modal.new_series.grade') }}
              </label>

              <select v-model="gradeId"
                      id="grade-select"
                      class="form-control"
                      :disabled="gradeSelectDisabled">
                <option disabled value="">
                  {{ $t('publishers.modal.new_series.grade_placeholder') }}
                </option>
                <option v-for="gId in gradeIds.filter(id => id !== '0' && id !== '9999')"
                        :key="gId"
                        :value="gId">
                  {{ getTranslatedName(gradeMap[gId]) }}
                </option>
              </select>
            </div>          

            <!-- domain select -->
            <div class="form-group" v-show="showDomainSelect">
              <label for="domain-select"
                    class="typography--body-3 typography--color-secondary typography--font-weight-600">
                {{ $t('publishers.modal.chapter_edit.domain') }}
              </label>

              <select v-model="domainId"
                      id="domain-select"
                      class="form-control"
                      @change="onDomainSelect"
                      :disabled="domainSelectDisabled">
                <option disabled value="">
                  {{ $t('publishers.modal.chapter_edit.domain_placeholder') }}
                </option>
                <option v-for="domain in domainList" :key="domain.id" :value="domain.id">
                  {{ domain.name }}
                </option>
              </select>
            </div>

            <!-- domain concept select -->
            <div class="form-group" v-show="showDomainConceptSelect">
              <label for="domain-concept-select"
                    class="typography--body-3 typography--color-secondary typography--font-weight-600">
                {{ $t('publishers.modal.chapter_edit.domain_concept') }}
              </label>

              <select v-model="domainConceptId"
                      id="domain-concept-select"
                      class="form-control">
                <option disabled value="">
                  {{ $t('publishers.modal.chapter_edit.domain_concept_placeholder') }}
                </option>
                <option v-for="concept in domainConceptList" :key="concept.id" :value="concept.id">
                  {{ concept.name }}
                </option>
              </select>
            </div>

            <!-- title input -->
            <div class="form-group">
              <label for="video-lecture-title-input"
                    class="typography--body-3 typography--color-secondary typography--font-weight-600">
                {{ $t('publishers.modal.new_series.name') }}
              </label>

              <input 
                type="text"
                id="video-lecture-title-input"
                class="form-control"
                maxlength="50"
                :placeholder="$t('publishers.modal.new_series.name_placeholder')"
                v-model.trim="lectureTitle"
              >
            </div>

            <!-- description textarea -->
            <div class="form-group">
              <label for="video-lecture-description-input"
                    class="typography--body-3 typography--color-secondary typography--font-weight-600">
                {{ $t('publishers.modal.new_series.description') }}
              </label>

              <textarea 
                id="video-lecture-description-input"
                class="form-control"
                rows="5"
                maxlength="500"
                :placeholder="$t('publishers.modal.new_series.description_placeholder')"
                v-model="lectureDescription"
              />
            </div>

            <!-- share level switch -->
            <div class="form-group pb-4">
              <label class="typography--body-3 typography--color-secondary typography--font-weight-600">
                {{ $t('publishers.modal.edit_video_lecture.share_level') }}
              </label>

              <div class="mt-6">
                <ToggleSwitch
                  id="share-level-switch"
                  @change="toggleShareLevel"
                  :model-value="shareLevel"
                  :check-value="1">
                  <span slot="label">{{ getShareLevelLabel(shareLevel || 2) }}</span>
                </ToggleSwitch>
              </div>
            </div>

            <div class="text-center mt-2" v-if="lecture">
              <button class="btn btn-primary btn-full edit-video-lecture__create-btn"
                      :disabled="newLectureDisabled"
                      @click="update()">
                <span v-if="!busy">{{ $t('common.save') }}</span>
                <LoadingBounce v-else></LoadingBounce>
              </button>
            </div>

            <div class="text-center mt-2" v-else>
              <button class="btn btn-primary btn-full edit-video-lecture__create-btn"
                      :disabled="newLectureDisabled"
                      @click="create()">
                <span v-if="!busy">{{ $t('video_lectures.new_video_course.create') }}</span>
                <LoadingBounce v-else></LoadingBounce>
              </button>
            </div>

            <div class="text-center mt-2">
              <router-link class="btn btn-secondary btn-full edit-video-lecture__cancel-btn typography--color-secondary"
                           :to="prevLink">
                {{ $t('btn.redirect_to_root') }}
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { mapGetters, mapActions } from 'vuex';
  import { isNil, isEmpty, map, flatten, trim, uniq } from 'lodash';
  import publisherOnly from '@/mixins/publisherOnly';
  import labels from '@/mixins/labels';

  export default {
    name: 'edit-video-lecture',

    mixins: [
      publisherOnly,
      labels,
    ],

    data() {
      return {
        subjectId: '',
        gradeId: '',
        domainId: '',
        domainConceptId: '',
        unitId: '',
        lectureTitle: '',
        lectureDescription: '',
        shareLevel: 2,
        domainList: [],
        domainConceptList: [],
        loading: false,
        busy: false,
      };
    },

    computed: {
      ...mapGetters([
        'recentVideoUploadInfo',
        'videoLectures',
        'videoCourses',
        'subjectMap',
        'subjectIds',
        'gradeMap',
        'gradeIds',
      ]),

      lecture() {
        return this.videoLectures[this.$route.params.lecture_id];
      },

      course() {
        return this.videoCourses[this.$route.params.course_id];
      },

      videoEmbed() {
        if (this.lecture && this.lecture.video && this.lecture.video.embed) {
          return this.lecture.video.embed;
        }

        if (this.recentVideoUploadInfo && this.recentVideoUploadInfo.embed) {
          return this.recentVideoUploadInfo.embed;
        }

        return null;
      },

      prevLink() {
        if (isNil(this.course) && isNil(this.lecture)) {
          return '/video-lectures';
        }

        if (isNil(this.course)) {
          return `/video-lectures/${this.lecture.id}`;
        }

        if (isNil(this.lecture)) {
          return `/video-courses/${this.course.id}`;
        }

        return `/video-courses/${this.course.id}/lectures/${this.lecture.id}`;
      },

      showDomainSelect() {
        return !isEmpty(this.domainList);
      },

      showDomainConceptSelect() {
        return !isEmpty(this.domainConceptList);
      },

      newLectureDisabled() {
        if (this.subjectId === '') {
          return true;
        }

        if (!isEmpty(this.domainList) && this.domainId === '') {
          return true;
        }

        if (isEmpty(this.lectureTitle)) {
          return true;
        }

        return false;
      },

      subjectSelectDisabled() {
        return this.course && !isNil(this.course.subject_id);
      },

      gradeSelectDisabled() {
        return this.course && !isNil(this.course.grade_id);
      },

      domainSelectDisabled() {
        return this.course && !isNil(this.course.domain_id);
      },
    },

    methods: {
      ...mapActions([
        'setRecentVideoUploadInfoInStore',
        'fetchLearningElementList',
        'fetchDomainConceptList',
        'createNewVideoLecture',
        'updateVideoLecture',
        'fetchVideoLecture',
        'fetchVideoCourse',
        'fetchDomainList',
      ]),

      fetchLearningElementNames(params) {
        const regex = /\s|#|@|~|!|\$|₩|%|\^|&|\*|\(|\)|-|\+|=|\[|\]|\{|\}|:|;|'|"|\?|\.|,|\/|<|>|`|\\|\|/gu;

        return this.fetchLearningElementList(params)
          .then(learningElementList => (
            uniq(flatten(map(learningElementList, elem => (
              map(elem.name.split('/'), name => (
                trim(name).replace(regex, '_')
              ))
            ))))
          ));
      },

      onSubjectSelect() {
        this.fetchDomainList({ subject_id: this.subjectId })
          .then((domainList) => {
            this.domainId = '';
            this.domainConceptId = '';
            this.domainList = domainList;
            this.domainConceptList = [];
          });
      },

      onDomainSelect() {
        this.fetchDomainConceptList({ domain_id: this.domainId })
          .then((domainConceptList) => {
            this.domainConceptId = '';
            this.domainConceptList = domainConceptList;
          });
      },

      toggleShareLevel(value) {
        this.shareLevel = (value ? 1 : 2);
      },

      create() {
        if (!this.busy) {
          this.busy = true;
  
          const params = {
            title: this.lectureTitle,
            description: this.lectureDescription || undefined,
            subject_id: this.subjectId || undefined,
            grade_id: this.gradeId === 'null' ? null : (this.gradeId || undefined),
            domain_id: this.domainId || undefined,
            domain_concept_id: this.domainConceptId || undefined,
            video_course_id: this.$route.params.course_id || undefined,
            position: this.course ? this.course.lecture_count + 1 : undefined,
            share_level: this.shareLevel,
            external_video_id: this.recentVideoUploadInfo.video_id,
            external_video_embed: this.recentVideoUploadInfo.embed,
          };
  
          this.createNewVideoLecture(params)
            .then(() => (
              this.$router.replace(this.prevLink)
            ));
        }
      },

      update() {
        if (!this.busy) {
          this.busy = true;
  
          const params = {
            id: this.$route.params.lecture_id,
            title: this.lectureTitle,
            description: this.lectureDescription || undefined,
            subject_id: this.subjectId || undefined,
            grade_id: this.gradeId === 'null' ? null : (this.gradeId || undefined),
            domain_id: this.domainId || undefined,
            domain_concept_id: this.domainConceptId || undefined,
            share_level: this.shareLevel,
          };
  
          this.updateVideoLecture(params)
            .then(() => {
              this.busy = false;
            });
        }
      },
    },

    beforeMount() {
      this.loading = true;

      const lectureId = this.$route.params.lecture_id;
      const courseId = this.$route.params.course_id;

      return Promise.resolve()
        .then(() => {
          if (!isNil(courseId)) {
            return this.fetchVideoCourse(courseId);
          }

          return null;
        })
        .then(() => {
          if (!isNil(this.course)) {
            this.subjectId = this.course.subject_id;
            this.gradeId = this.course.grade_id;
            this.domainId = this.course.domain_id;
          }
        })
        .then(() => {
          if (!isNil(lectureId) && isEmpty(this.videoLectures[lectureId])) {
            return this.fetchVideoLecture(lectureId);
          }

          return null;
        })
        .then(() => {
          if (!isNil(this.lecture)) {
            this.subjectId = this.lecture.subject_id || '';
            this.gradeId = this.lecture.grade_id;
            this.domainId = this.lecture.domain_id || '';
            this.domainConceptId = this.lecture.domain_concept_id || '';
            this.unitId = this.lecture.unit_id;
            this.lectureTitle = this.lecture.title;
            this.lectureDescription = this.lecture.description;
            this.shareLevel = this.lecture.share_level;
          }
        })
        .then(() => {
          if (this.subjectId) {
            return this.fetchDomainList({ subject_id: this.subjectId })
              .then((domainList) => {
                this.domainList = domainList;
              });
          }

          return null;
        })
        .then(() => {
          if (this.domainId) {
            return this.fetchDomainConceptList({ domain_id: this.domainId })
              .then((domainConceptList) => {
                this.domainConceptList = domainConceptList;
              });
          }

          return null;
        })
        .then(() => {
          if (isNil(this.videoEmbed)) {
            return this.$router.replace(this.prevLink);
          }

          this.loading = false;
          return null;
        });
    },

    beforeDestroy() {
      this.setRecentVideoUploadInfoInStore(null);
    },
  };
</script>

<style lang="scss">
  .edit-video-lecture {
    .edit-video-lecture__body {
      max-width: 400px;
      padding-bottom: 100px;
    }

    .edit-video-lecture__header-title {
      margin-top: 60px;
      font-size: 28px;
      line-height: 1.79;
    }

    .edit-video-lecture__video-preview {
      margin-top: 40px;

      iframe {
        width: 100% !important;
        max-height: 285px !important;
      }
    }

    .edit-video-lecture__form {
      margin-top: 30px;

      .form-group .form-control {
        font-size: 14px;
      }

      .form-group input.form-control {        
        height: 47px;
      }
    }

    .edit-video-lecture__create-btn,
    .edit-video-lecture__cancel-btn {
      font-size: 16px;
      line-height: 1.88;
      font-weight: 400;
      padding-top: 5px;
      padding-bottom: 5px;
      border-radius: 4px;
    }
  }
</style>



// WEBPACK FOOTER //
// src/app/video-lectures/views/EditVideoLecture.vue